import React from "react"
import BoardColumnSlider from "./BoardColumnSlider"
import BoardColumnChooser from "./BoardColumnChooser"
import BoardColumn from "./BoardColumn"
import { DragDropContext } from "react-beautiful-dnd"
import { useIsSmallScreen } from "../../hooks/use-is-small-screen"
import { getTranslatedDefaultColumnNames } from "../utils"
import { useTranslations } from "../hooks/use-translations"
import { useBoard } from "../hooks/use-board"
import { BoardViewModelV2 } from "../api/v2/board-view-model"

type BoardProps = {
  board: BoardViewModelV2
  canAddTasks: boolean
  canAddTodos: boolean
}

export default function Board(props: BoardProps) {
  const isSmallScreen = useIsSmallScreen()

  if (isSmallScreen) {
    return <BoardMobile {...props} />
  }

  return <BoardDefault {...props} />
}

const BoardMobile = ({ board, canAddTasks, canAddTodos }: BoardProps) => {
  const translations = useTranslations()
  const maxVisibleColumns = board.visibleColumnIds.length
  const [visibleColumnIndex, setVisibleColumnIndex] = React.useState(0)
  const { handleAddTask, handleAddTodo, handleDragEnd, handleDragUpdate, handleUpdateColumnName } = useBoard(board)

  if (board.hasAllColumnsHidden) {
    return <BoardColumnChooser boardId={board.id} boardType={board.type} buttonVariant="button" />
  }

  const showNextColumn = () =>
    setVisibleColumnIndex((prev) => {
      const desiredNext = prev + 1
      return desiredNext >= maxVisibleColumns ? prev : desiredNext
    })

  const showPrevColumn = () =>
    setVisibleColumnIndex((prev) => {
      const desiredPrev = prev - 1
      return desiredPrev < 0 ? prev : desiredPrev
    })

  const showSelectedColumn = (index: number) => {
    setVisibleColumnIndex(index >= maxVisibleColumns || index < 0 ? 0 : index)
  }

  const _selectedColumn = board.columnsById[board.visibleColumnIds[visibleColumnIndex]]
  const translatedDefaultColumn = getTranslatedDefaultColumnNames({
    columnName: _selectedColumn.defaultName,
    translations,
  })

  const selectedColumn = {
    ..._selectedColumn,
    name: _selectedColumn.name || translatedDefaultColumn,
  }

  return (
    <>
      <BoardColumnSlider
        board={board}
        showNextColumn={showNextColumn}
        showPrevColumn={showPrevColumn}
        showSelectedColumn={showSelectedColumn}
        selectedColumn={selectedColumn}
      />
      <DragDropContext onDragEnd={handleDragEnd} onDragUpdate={handleDragUpdate}>
        <div
          style={{ display: "flex", alignItems: "flex-start", flexWrap: "nowrap", flexGrow: 1 }}
          data-test="board-container"
        >
          <BoardColumn
            board={board}
            column={selectedColumn}
            canAddTasks={canAddTasks}
            canAddTodos={canAddTodos}
            key={selectedColumn.id}
            onAddTask={handleAddTask}
            onAddTodo={handleAddTodo}
            onSaveColumnName={handleUpdateColumnName}
          />
        </div>
      </DragDropContext>
    </>
  )
}

const BoardDefault = ({ board, canAddTasks, canAddTodos }: BoardProps) => {
  const { handleDragUpdate, handleDragEnd, handleAddTask, handleAddTodo, handleUpdateColumnName } = useBoard(board)

  return (
    <DragDropContext onDragEnd={handleDragEnd} onDragUpdate={handleDragUpdate}>
      <div
        style={{ display: "flex", alignItems: "flex-start", flexWrap: "nowrap", flexGrow: 1 }}
        data-test="board-container"
      >
        {board.visibleColumnIds.map((columnId) => {
          const column = board.columnsById[columnId]
          return (
            <BoardColumn
              board={board}
              column={column}
              canAddTasks={canAddTasks}
              canAddTodos={canAddTodos}
              key={column.id}
              onAddTask={handleAddTask}
              onAddTodo={handleAddTodo}
              onSaveColumnName={handleUpdateColumnName}
            />
          )
        })}
      </div>
    </DragDropContext>
  )
}
